// Thin fetch wrapper for the WSB API.
// API base + device token live in localStorage so the game stays authenticated
// across reloads. Token is set by Device Setup (paste from dashboard).

const API_BASE_KEY = 'wsb_api_base';
const DEVICE_TOKEN_KEY = 'wsb_device_token';

export function apiBase(): string {
  return localStorage.getItem(API_BASE_KEY) || (import.meta.env.VITE_API_BASE ?? 'http://localhost:8787');
}
export function setApiBase(url: string) {
  localStorage.setItem(API_BASE_KEY, url);
}
export function deviceToken(): string | null {
  return localStorage.getItem(DEVICE_TOKEN_KEY);
}
export function setDeviceToken(token: string) {
  localStorage.setItem(DEVICE_TOKEN_KEY, token);
}
export function clearDeviceToken() {
  localStorage.removeItem(DEVICE_TOKEN_KEY);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = deviceToken();
  const headers = new Headers(init?.headers);
  headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);
  const res = await fetch(`${apiBase()}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export interface SpinResult {
  won: boolean;
  prize: { id: number; name: string; emoji: string; image_url: string | null } | null;
  spin_id: number;
  server_time: number;
}

export interface PaytablePrize {
  id: number;
  name: string;
  emoji: string;
  image_url: string | null;
  win_rate: number;
  max_qty: number;
  qty_remaining: number;
}

export interface GameConfig {
  event: {
    id: number;
    name: string;
    in_game_pin: string;
    bg_image_url: string | null;
    logo_image_url: string | null;
    title_image_url: string | null;
  };
  device: { id: number; name: string };
}

export interface IssuedTicket {
  ticket_uuid: string;
  won: boolean;
  prize: { id: number; name: string; emoji: string; image_url: string | null } | null;
  expires_at: number;
}

export const api = {
  spin: () => request<SpinResult>('/spin', { method: 'POST' }),
  tickets: (count = 20, ttl_seconds = 24 * 3600) =>
    request<{ issued_at: number; expires_at: number; tickets: IssuedTicket[] }>('/spin/tickets', {
      method: 'POST',
      body: JSON.stringify({ count, ttl_seconds }),
    }),
  redeem: (redemptions: Array<{ ticket_uuid: string; played_at: number }>) =>
    request<{ accepted: string[]; rejected: Array<{ ticket_uuid: string; reason: string }> }>(
      '/spin/redeem',
      { method: 'POST', body: JSON.stringify({ redemptions }) },
    ),
  paytable: () => request<{ prizes: PaytablePrize[] }>('/spin/paytable'),
  config: () => request<GameConfig>('/spin/config'),
};
